/*
 * uls-part-pdp.js: behaviour for sections/uls-part-pdp.liquid (ULS / ABI part PDP, R3 v2).
 * Theme asset, loaded with `defer` by the section only. Vanilla JS, no dependencies, ES2019.
 *
 * Spec: Design Handoffs/ULS-Parts-PDP/design_handoff_uls_part_pdp_r3_v2/README.md ("Interactions & behavior")
 * Hooks: review/uls-part-pdp-build-2026-09-27/DOM-CONTRACT.md (+ L/ and J/ CONTRACT-ADDITIONS.md)
 *
 * Structure
 *   1. core: pure logic (matching, fit phrases, bar text, money, storage, help URLs, doc/add-on plans).
 *      Exported for node tests: require('./uls-part-pdp.js'). Never touches window/document at require time.
 *   2. DOM: one controller per [data-updp] root. Liquid renders every state; this file only sets
 *      data-* attributes, toggles `hidden`, fills [data-slot] text (textContent only), rewrites hrefs,
 *      moves existing nodes and clones the section's <template>s.
 *
 * Page-wide machine state: document event `updp:machine` (detail {id, name, catalog_id, family, status,
 * restored} or null). Every module re-renders from it; nothing else writes machine state.
 */
(function () {
  'use strict';

  /* ======================================================================================
   * 1. CORE (pure)
   * ==================================================================================== */
  var core = (function () {
    /* English defaults for every string J writes (copy deck; island `strings` from Liquid win). Text that
       Liquid renders with a [data-slot] for the model (scope line, Replaces line, doc headings, add-on note…)
       is not duplicated here. */
    var DEFAULT_STRINGS = {
      /* fit row */
      panel_check: 'Check your machine',
      panel_change: 'Change machine',
      fit_listed_title: 'Listed for {model}',
      fit_fits_title: 'Fits {model}',
      fit_notlisted_title: 'Not listed for {model}',
      fit_confirm_title: 'Fit not confirmed for {model}',
      fit_evidence_uls: 'In Universal Laser’s parts list · {family}',
      fit_evidence_abi: 'On ABI’s fit list for this part · {family}',
      fit_evidence_confirm: 'Confirm before you order.',
      fit_note_default: 'It may still fit. We’ll confirm before you order.',
      fit_view_sku: 'View {sku}',
      condition_suffix: ' · {condition}',
      from_last_visit: ' · from your last visit',
      pill_listed: '✓ Listed',
      pill_fits: '✓ Fits',
      pill_notlisted: 'Not listed',
      pill_confirm: 'Confirm fit',
      sheet_matches: '{n} models match “{query}”',
      sheet_matches_one: '1 model matches “{query}”',
      /* bars */
      bar_listed: 'Listed for {model}',
      bar_fits: 'Fits {model}',
      bar_notconfirmed: 'Fit not confirmed for {model}',
      /* OEM and ABI options */
      opt_listed: 'Listed for {model}',
      opt_fits: 'Fits {model}',
      opt_notlisted: 'Not listed for {model}',
      opt_notlisted_abi: 'Not on ABI’s fit list for {model}',
      options_note: 'For the {model}, Universal Laser lists a different {noun}: {sku}.',
      /* purchase */
      atc_one: 'Add to Cart',
      atc_many: 'Add {n} to cart',
      each: '{n} × {unit}',
      added: 'Added {n} to your cart',
      added_live: 'Added to cart',
      add_failed: 'We couldn’t add this to your cart. Check your connection and try again.',
      sku_copied: 'SKU copied',
      /* compatibility */
      matches_one: '1 match',
      matches_many: '{n} matches',
      acc_compat_none: 'View all compatible models',
      acc_compat_listed: '{model} is listed',
      acc_compat_notlisted: '{model} isn’t listed',
      /* gallery (fallback when there is no counter-current slot) */
      counter: '{n} of {total}'
    };

    function norm(t) {
      return String(t == null ? '' : t).toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    function format(tpl, vars) {
      return String(tpl == null ? '' : tpl).replace(/\{([a-z_]+)\}/gi, function (m, k) {
        return vars && vars[k] != null ? String(vars[k]) : '';
      });
    }

    /* str(key, vars): island string when present, else the English default; unknown keys give ''. */
    function makeStr(strings) {
      var s = strings && typeof strings === 'object' ? strings : {};
      function raw(key) {
        if (typeof s[key] === 'string' && s[key] !== '') return s[key];
        return DEFAULT_STRINGS[key] != null ? DEFAULT_STRINGS[key] : '';
      }
      function str(key, vars) { return format(raw(key), vars); }
      str.raw = raw;
      return str;
    }

    function arr(v) { return Array.isArray(v) ? v : []; }
    function strList(v) {
      return arr(v).filter(function (x) { return typeof x === 'string' && x !== ''; });
    }

    /* Registry: README → Model registry. Accepts a top-level array, or {models|entries: [...]}. */
    function normalizeRegistry(json) {
      var list = Array.isArray(json) ? json
        : (json && typeof json === 'object' ? (Array.isArray(json.models) ? json.models : (Array.isArray(json.entries) ? json.entries : null)) : null);
      if (!list || !list.length) return null;
      var familyImages = json && !Array.isArray(json) && json.family_images && typeof json.family_images === 'object' ? json.family_images : null;
      var out = [];
      var byId = {};
      list.forEach(function (e) {
        if (!e || typeof e !== 'object') return;
        var id = e.id != null ? String(e.id) : '';
        var name = e.name != null ? String(e.name) : '';
        if (!id || !name || byId[id]) return;
        var family = e.family != null ? String(e.family) : '';
        var img = typeof e.family_image === 'string' && e.family_image ? e.family_image
          : (familyImages && typeof familyImages[family] === 'string' && familyImages[family] ? familyImages[family] : null);
        var aliases = strList(e.aliases);
        var entry = {
          id: id,
          name: name,
          catalog_id: e.catalog_id != null ? String(e.catalog_id) : '',
          family: family,
          family_image: img,
          aliases: aliases,
          status_hint: e.status_hint ? String(e.status_hint) : null,
          note: typeof e.note === 'string' ? e.note : '',
          order: out.length,
          keys: [norm(name)].concat(aliases.map(norm)).filter(function (k) { return k !== ''; })
        };
        out.push(entry);
        byId[id] = entry;
      });
      if (!out.length) return null;
      return { list: out, byId: byId };
    }

    /* Rank exact → starts-with → contains; ties keep registry order; at most `limit` (7); nothing under 2 chars. */
    function rank(reg, query, limit) {
      var q = norm(query);
      if (!reg || !reg.list || q.length < 2) return [];
      var out = [];
      reg.list.forEach(function (e) {
        var score = 99;
        for (var i = 0; i < e.keys.length; i++) {
          var k = e.keys[i];
          if (k === q) { score = 0; break; }
          var at = k.indexOf(q);
          if (at === 0) score = Math.min(score, 1);
          else if (at > 0) score = Math.min(score, 2);
        }
        if (score < 99) out.push({ entry: e, score: score });
      });
      out.sort(function (a, b) { return (a.score - b.score) || (a.entry.order - b.entry.order); });
      return out.slice(0, limit || 7);
    }

    /* The single exact match, if exactly one entry matches exactly (Enter with nothing highlighted). */
    function uniqueExact(results) {
      var exact = results.filter(function (r) { return r.score === 0; });
      return exact.length === 1 ? exact[0].entry : null;
    }

    /* Status for THIS product: listed (catalog id in fit) → confirm (status_hint) → notlisted. */
    function statusFor(entry, fit) {
      if (!entry) return 'none';
      if (entry.catalog_id && arr(fit).indexOf(entry.catalog_id) !== -1) return 'listed';
      if (entry.status_hint === 'confirm') return 'confirm';
      return 'notlisted';
    }

    function findNote(notes, catalogId) {
      if (!catalogId) return null;
      var list = arr(notes);
      for (var i = 0; i < list.length; i++) {
        var n = list[i];
        if (n && arr(n.models).indexOf(catalogId) !== -1) return n;
      }
      return null;
    }

    function brandOf(island) {
      var b = island && island.product && island.product.brand;
      return b === 'abi' ? 'abi' : 'oem';
    }

    /* Phrase + " · {condition}" unless the template already places {condition} itself. */
    function withCondition(str, key, vars, condition) {
      var tpl = str.raw(key);
      var text = format(tpl, vars);
      if (condition && tpl.indexOf('{condition}') === -1) text += leadSpace(str('condition_suffix', vars));
      return text;
    }
    /* suffix strings (" · …") keep their leading space even if an island value lost it */
    function leadSpace(s) {
      var t = String(s == null ? '' : s);
      return t && !/^\s/.test(t) ? ' ' + t : t;
    }

    /* README → Fit phrases. Returns every string the fit row and the bars need for one machine.
       settings.fit_condition_separate (island, optional): keep the condition out of the evidence line and
       show [data-updp-fit-cond] instead (README §E layout reading). Default: appended (Fit phrases table). */
    function fitResult(island, entry, str) {
      var status = statusFor(entry, island && island.fit);
      var abi = brandOf(island) === 'abi';
      var condition = island && typeof island.fit_condition === 'string' ? island.fit_condition : '';
      var separate = !!(island && island.settings && island.settings.fit_condition_separate === true);
      var vars = { model: entry.name, family: entry.family, condition: condition };
      var titleKey;
      var evKey;
      if (status === 'listed') {
        titleKey = abi ? 'fit_fits_title' : 'fit_listed_title';
        evKey = abi ? 'fit_evidence_abi' : 'fit_evidence_uls';
      } else {
        titleKey = status === 'confirm' ? 'fit_confirm_title' : 'fit_notlisted_title';
        evKey = 'fit_evidence_confirm';
      }
      var barKey = status === 'listed' ? (abi ? 'bar_fits' : 'bar_listed') : 'bar_notconfirmed';
      var note = '';
      var alt = null;
      var pointer = null;
      if (status === 'listed') {
        var ln = findNote(island && island.listed_notes, entry.catalog_id);
        note = ln && typeof ln.text === 'string' ? ln.text : '';
      } else {
        pointer = findNote(island && island.not_listed_notes, entry.catalog_id);
        if (pointer && typeof pointer.text === 'string') note = pointer.text;
        if (!note && status === 'confirm' && entry.note) note = entry.note;
        if (!note) note = str('fit_note_default', vars);
        if (pointer && pointer.product_sku && pointer.product_url) alt = { sku: String(pointer.product_sku), url: String(pointer.product_url) };
      }
      return {
        status: status,
        title: str(titleKey, vars),
        evidence: separate ? format(str.raw(evKey), vars) : withCondition(str, evKey, vars, condition),
        bar: withCondition(str, barKey, vars, condition),
        condition: condition,
        conditionSeparate: separate && !!condition,
        note: note,
        alt: alt,
        pointer: pointer,
        pill: pillText(status, abi, str)
      };
    }

    function pillText(status, abi, str) {
      if (status === 'listed') return str(abi ? 'pill_fits' : 'pill_listed');
      return str(status === 'confirm' ? 'pill_confirm' : 'pill_notlisted');
    }

    /* OEM and ABI options. catalogId: null/undefined = no machine; '' = a machine without a parts list
       (registry "confirm" entries), which is never in a scope or a fit list. */
    function scopeState(equivalence, catalogId) {
      if (catalogId == null) return 'none';
      return catalogId && equivalence && arr(equivalence.scope).indexOf(catalogId) !== -1 ? 'in' : 'out';
    }
    function optionFit(equivalence, brand, catalogId) {
      if (catalogId == null) return 'none';
      var side = equivalence && equivalence[brand];
      return catalogId && side && arr(side.fit).indexOf(catalogId) !== -1 ? 'listed' : 'notlisted';
    }
    /* Pointer note under the options module when neither card lists the machine. The sentence renders once,
       with the SKU itself as the link: { before, sku, after, href, full }. OEM page: the copy-deck sentence
       "For the {model}, Universal Laser lists a different {noun}: {product_sku}." ABI page: the note's own text
       (the copy-deck sentence would claim Universal Laser lists an ABI part). href: product_url, else
       /products/{product_handle}, else a product search for the SKU. No product_sku → plain text, no link. */
    function optionsNoteParts(island, p, model, str) {
      if (!p) return null;
      var sku = p.product_sku ? String(p.product_sku) : '';
      var noun = island && island.product && island.product.noun ? String(island.product.noun) : '';
      var vars = { model: model, noun: noun, sku: sku, product_sku: sku };
      var text = typeof p.text === 'string' ? p.text : '';
      var sentence = sku && brandOf(island) !== 'abi' ? str('options_note', vars) : (text || (sku ? str('options_note', vars) : ''));
      if (!sku) return { before: sentence, sku: '', after: '', href: null, full: sentence };
      var href = p.product_url ? String(p.product_url)
        : (p.product_handle ? '/products/' + encodeURIComponent(String(p.product_handle)) : '/search?' + new URLSearchParams({ type: 'product', q: sku }).toString());
      var at = sentence.lastIndexOf(sku);
      if (at === -1) return { before: sentence, sku: sku, after: '', href: href, full: sentence + ' ' + sku };
      return { before: sentence.slice(0, at).replace(/\s+$/, ''), sku: sku, after: sentence.slice(at + sku.length), href: href, full: sentence };
    }
    function optionFitText(brand, status, model, str) {
      if (status === 'none') return '';
      if (status === 'listed') return str(brand === 'abi' ? 'opt_fits' : 'opt_listed', { model: model });
      return str(brand === 'abi' ? 'opt_notlisted_abi' : 'opt_notlisted', { model: model });
    }

    /* Quantity and money */
    function clampQty(v) {
      var n = typeof v === 'number' ? v : parseInt(String(v == null ? '' : v).trim(), 10);
      if (!isFinite(n) || isNaN(n)) return null;
      n = Math.floor(n);
      return Math.max(1, Math.min(99, n));
    }
    function atcLabel(qty, str) {
      return qty > 1 ? str('atc_many', { n: qty }) : str('atc_one');
    }
    var fmtCache = {};
    function money(cents, currency, locale) {
      var cur = currency || 'USD';
      var loc = locale || 'en-US';
      var value = (Number(cents) || 0) / 100;
      try {
        var k = loc + '|' + cur;
        if (!fmtCache[k]) fmtCache[k] = new Intl.NumberFormat(loc, { style: 'currency', currency: cur });
        return fmtCache[k].format(value);
      } catch (e) {
        return (cur === 'USD' ? '$' : cur + ' ') + value.toFixed(2);
      }
    }
    function barTotals(priceCents, qty, currency, str, locale) {
      var q = clampQty(qty) || 1;
      return {
        total: money(priceCents * q, currency, locale),
        each: q > 1 ? str('each', { n: q, unit: money(priceCents, currency, locale) }) : '',
        label: atcLabel(q, str)
      };
    }
    /* Bars: the availability line shows with no machine, and always for backordered parts. The mobile
       bar's "{n} × {unit} · {availability}" line keeps it always (README Mobile, boards 07 C2 / 11). */
    function barAvailVisible(bar, hasMachine, avail) {
      if (bar === 'mbar') return true;
      return !hasMachine || avail === 'backordered';
    }
    /* Geometry for the bars (QA A8). An element is "out" only once it has been scrolled past: its bottom is
       at or above the sticky header's bottom edge (bottom <= header offset). Still below the fold is NOT out
       (README: the bar "appears once the main Add to Cart scrolls out of view"; board 04 has no bar at load).
       A rect with no size and no position means "not laid out" (display:none, jsdom) and never counts as out. */
    function laidOut(r) { return !!r && (r.width > 0 || r.height > 0 || r.top !== 0 || r.bottom !== 0); }
    function outOfView(r, headerOffset) {
      if (!laidOut(r)) return false;
      return r.bottom <= (headerOffset || 0);
    }
    /* The footer is "near" once its top is within `margin` px below the viewport bottom (and it isn't fully above). */
    function footerNear(r, vh, margin) {
      if (!laidOut(r)) return false;
      return r.top < vh + (margin || 0) && r.bottom > 0;
    }

    /* Saved machines: localStorage["abi.machines"] = {v: 2, current, recent: [≤3]} */
    var STORE_KEY = 'abi.machines';
    var V1_KEY = 'abi.machine';
    function emptyState() { return { v: 2, current: null, recent: [] }; }
    function cleanState(o) {
      if (!o || typeof o !== 'object' || o.v !== 2) return emptyState();
      var recent = [];
      arr(o.recent).forEach(function (id) {
        if (typeof id === 'string' && id && recent.indexOf(id) === -1 && recent.length < 3) recent.push(id);
      });
      return { v: 2, current: typeof o.current === 'string' && o.current ? o.current : null, recent: recent };
    }
    function readMachines(storage) {
      if (!storage) return emptyState();
      try {
        var raw = storage.getItem(STORE_KEY);
        if (!raw) return emptyState();
        return cleanState(JSON.parse(raw));
      } catch (e) {
        return emptyState();
      }
    }
    function writeMachines(storage, state) {
      if (!storage) return false;
      try {
        var s = cleanState(state);
        storage.setItem(STORE_KEY, JSON.stringify({ v: 2, current: s.current, recent: s.recent }));
        return true;
      } catch (e) {
        return false;
      }
    }
    function chooseMachine(state, id) {
      var s = cleanState(state);
      return { v: 2, current: id, recent: [id].concat(s.recent.filter(function (x) { return x !== id; })).slice(0, 3) };
    }
    function clearCurrent(state) {
      var s = cleanState(state);
      return { v: 2, current: null, recent: s.recent.slice() };
    }
    /* V1 value {id, label} → a registry entry: by id, then by exact normalized label/id against names and aliases. */
    function resolveV1(old, reg) {
      if (!reg || old == null) return null;
      var id = typeof old === 'string' ? old : (old && old.id != null ? String(old.id) : '');
      var label = old && typeof old === 'object' && old.label != null ? String(old.label) : '';
      if (id && reg.byId[id]) return reg.byId[id];
      var tries = [norm(label), norm(id)].filter(function (k) { return k.length >= 2; });
      for (var t = 0; t < tries.length; t++) {
        for (var i = 0; i < reg.list.length; i++) {
          if (reg.list[i].keys.indexOf(tries[t]) !== -1) return reg.list[i];
        }
      }
      return null;
    }
    /* Migrate V1 once, then remove it. Needs the registry; returns the resulting state or null when nothing ran. */
    function migrateV1(storage, reg) {
      if (!storage || !reg) return null;
      try {
        var raw = storage.getItem(V1_KEY);
        if (raw == null) return null;
        var old = null;
        try { old = JSON.parse(raw); } catch (e) { old = raw; }
        var hadV2 = !!storage.getItem(STORE_KEY);
        var state = readMachines(storage);
        var entry = resolveV1(old, reg);
        if (entry && !hadV2) {
          state = chooseMachine(state, entry.id);
          writeMachines(storage, state);
        }
        storage.removeItem(V1_KEY);
        return state;
      } catch (e) {
        return null;
      }
    }

    /* Help and quote links: parameter order topic, reason, sku, model, qty, from; empty ones omitted.
       changes[key]: undefined = leave as is, null or '' = remove, value = set. */
    var HELP_ORDER = ['topic', 'reason', 'sku', 'model', 'qty', 'from'];
    function rewriteHelpUrl(href, changes) {
      var h = String(href == null ? '' : href);
      var hash = '';
      var hi = h.indexOf('#');
      if (hi !== -1) { hash = h.slice(hi); h = h.slice(0, hi); }
      var query = '';
      var qi = h.indexOf('?');
      if (qi !== -1) { query = h.slice(qi + 1); h = h.slice(0, qi); }
      var vals = {};
      var extra = [];
      new URLSearchParams(query).forEach(function (v, k) {
        if (HELP_ORDER.indexOf(k) !== -1) { if (!(k in vals)) vals[k] = v; }
        else extra.push([k, v]);
      });
      Object.keys(changes || {}).forEach(function (k) {
        var v = changes[k];
        if (v === undefined) return;
        vals[k] = v == null ? '' : String(v);
        if (HELP_ORDER.indexOf(k) === -1) {
          extra = extra.filter(function (p) { return p[0] !== k; });
          extra.push([k, vals[k]]);
        }
      });
      var out = new URLSearchParams();
      HELP_ORDER.forEach(function (k) { if (vals[k] != null && vals[k] !== '') out.append(k, vals[k]); });
      extra.forEach(function (p) { if (p[1] != null && p[1] !== '') out.append(p[0], p[1]); });
      var s = out.toString();
      return h + (s ? '?' + s : '') + hash;
    }
    /* What each help link carries for the current machine and quantity. */
    function helpChanges(kind, hasQty, machine, qty) {
      /* "Help identify my machine": the customer is naming a machine, so no model and no reason */
      if (kind === 'identify') return { model: null };
      var c = { model: machine ? machine.name : null };
      if (kind === 'quote' || hasQty) c.qty = qty;
      if (kind === 'ask') c.reason = machine && machine.status !== 'listed' ? 'confirm-fit' : null;
      return c;
    }

    /* You may also need (README rule 7). No machine (null): items listed for EVERY model of this part.
       Machine: items listed for its catalog id ('' = no parts list → nothing is listed for it). */
    function addonVisible(addonFit, productFit, catalogId) {
      var f = arr(addonFit);
      if (catalogId != null) return !!catalogId && f.indexOf(catalogId) !== -1;
      return arr(productFit).every(function (id) { return f.indexOf(id) !== -1; });
    }

    /* Installation & downloads with a machine: mine (install guides first, then procedures/drawings,
       then the approved glance for the family, then care), mymanual, others. An empty applies_to means
       "every model on this part's fit list" (L/CONTRACT-ADDITIONS A); with no fit list either, the doc stays put.
       catalogId null = no machine; '' = a machine without a parts list (nothing matches it). A glance shows only
       for a machine listed for this part whose family the entry names. */
    var TYPE_RANK = { install_guide: 0, procedure: 1, drawing: 2, document: 2, care: 4 };
    function docsPlan(docs, glances, catalogId, family, productFit) {
      var mine = [];
      var mymanual = [];
      var others = [];
      var stay = [];
      var glanceKeys = [];
      var listed = !!catalogId && strList(productFit).indexOf(catalogId) !== -1;
      arr(docs).forEach(function (d, i) {
        if (!d || !d.key) return;
        var applies = strList(d.applies_to);
        if (!applies.length) applies = strList(productFit);
        if (catalogId == null || !applies.length) { stay.push(d.key); return; }
        if (catalogId && applies.indexOf(catalogId) !== -1) {
          if (d.doc_type === 'manual') mymanual.push(d.key);
          else mine.push({ key: d.key, kind: 'doc', rank: TYPE_RANK[d.doc_type] != null ? TYPE_RANK[d.doc_type] : 2, i: i });
        } else {
          others.push(d.key);
        }
      });
      arr(glances).forEach(function (g, i) {
        if (!g || !g.key || !listed || !family) return;
        if (strList(g.families).indexOf(family) !== -1) {
          glanceKeys.push(g.key);
          mine.push({ key: g.key, kind: 'glance', rank: 3, i: i });
        }
      });
      mine.sort(function (a, b) { return (a.rank - b.rank) || (a.i - b.i); });
      return {
        mine: mine.map(function (m) { return { key: m.key, kind: m.kind }; }),
        mymanual: mymanual,
        others: others,
        stay: stay,
        glance: glanceKeys
      };
    }

    /* Compatibility search: a chip matches when its name or any registry alias contains the query. */
    function chipMatches(name, aliases, q) {
      if (!q) return false;
      if (norm(name).indexOf(q) !== -1) return true;
      return arr(aliases).some(function (a) { return norm(a).indexOf(q) !== -1; });
    }
    function matchesText(n, str) {
      return n === 1 ? str('matches_one', { n: 1, m: 1 }) : str('matches_many', { n: n, m: n });
    }
    function sheetMatchesText(n, query, str) {
      var vars = { n: n, m: n, q: query, query: query };
      return n === 1 ? str('sheet_matches_one', vars) : str('sheet_matches', vars);
    }
    function accSummaryKey(status) {
      if (!status || status === 'none') return 'acc_compat_none';
      return status === 'listed' ? 'acc_compat_listed' : 'acc_compat_notlisted';
    }

    return {
      DEFAULT_STRINGS: DEFAULT_STRINGS,
      leadSpace: leadSpace,
      STORE_KEY: STORE_KEY,
      V1_KEY: V1_KEY,
      HELP_ORDER: HELP_ORDER,
      norm: norm,
      format: format,
      makeStr: makeStr,
      normalizeRegistry: normalizeRegistry,
      rank: rank,
      uniqueExact: uniqueExact,
      statusFor: statusFor,
      findNote: findNote,
      brandOf: brandOf,
      fitResult: fitResult,
      pillText: pillText,
      scopeState: scopeState,
      optionFit: optionFit,
      optionFitText: optionFitText,
      optionsNoteParts: optionsNoteParts,
      clampQty: clampQty,
      atcLabel: atcLabel,
      money: money,
      barTotals: barTotals,
      barAvailVisible: barAvailVisible,
      outOfView: outOfView,
      footerNear: footerNear,
      readMachines: readMachines,
      writeMachines: writeMachines,
      chooseMachine: chooseMachine,
      clearCurrent: clearCurrent,
      resolveV1: resolveV1,
      migrateV1: migrateV1,
      rewriteHelpUrl: rewriteHelpUrl,
      helpChanges: helpChanges,
      addonVisible: addonVisible,
      docsPlan: docsPlan,
      chipMatches: chipMatches,
      matchesText: matchesText,
      sheetMatchesText: sheetMatchesText,
      accSummaryKey: accSummaryKey
    };
  })();

  if (typeof module === 'object' && module.exports) module.exports = core;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  /* ======================================================================================
   * 2. DOM
   * ==================================================================================== */
  var SECTION_IDS = ['compatibility', 'specs-contents', 'installation'];
  var HEADING_IDS = { compatibility: 'updp-h-compatibility', 'specs-contents': 'updp-h-specs', installation: 'updp-h-installation' };
  var REGISTRY_CACHE_KEY = 'updp.registry.v1';
  var FOOTER_MARGIN = 80; /* px below the viewport at which the footer counts as near (mobile bar hides) */
  var registryPromises = {};
  var memoryMachines = null; /* used when localStorage is blocked */

  function qs(el, sel) { return el ? el.querySelector(sel) : null; }
  function qsa(el, sel) { return el ? Array.prototype.slice.call(el.querySelectorAll(sel)) : []; }
  function setHidden(el, hide) {
    if (!el) return;
    if (hide) { if (!el.hasAttribute('hidden')) el.setAttribute('hidden', ''); }
    else if (el.hasAttribute('hidden')) el.removeAttribute('hidden');
  }
  function setText(el, t) { if (el) el.textContent = t == null ? '' : String(t); }
  function fillSlots(scope, name, text) {
    qsa(scope, '[data-slot="' + name + '"]').forEach(function (el) { setText(el, text); });
  }
  function setAttr(el, name, value) {
    if (!el) return;
    if (value == null || value === false) el.removeAttribute(name);
    else el.setAttribute(name, value === true ? '' : String(value));
  }
  function mq(query) {
    try { return window.matchMedia ? window.matchMedia(query) : null; } catch (e) { return null; }
  }
  var mobileMq = mq('(max-width: 749.98px)');
  var reduceMq = mq('(prefers-reduced-motion: reduce)');
  function isMobile() { return !!(mobileMq && mobileMq.matches); }
  function reducedMotion() { return !!(reduceMq && reduceMq.matches); }
  function onMqChange(m, fn) {
    if (!m) return;
    if (m.addEventListener) m.addEventListener('change', fn);
    else if (m.addListener) m.addListener(fn);
  }
  function raf(fn) {
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(fn);
    else setTimeout(fn, 16);
  }
  /* Visible for focus purposes: not inside [hidden], and laid out when the engine does layout. */
  function hasLayout() {
    var de = document.documentElement;
    return !!(de && de.getClientRects && de.getClientRects().length > 0);
  }
  function rendered(el) {
    if (!el || !el.isConnected || el.closest('[hidden]')) return false;
    if (!hasLayout() || !el.getClientRects) return true;
    return el.getClientRects().length > 0;
  }
  function focusEl(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { try { el.focus(); } catch (e2) { /* not focusable */ } }
  }
  function localStore() { try { return window.localStorage; } catch (e) { return null; } }
  function sessionStore() { try { return window.sessionStorage; } catch (e) { return null; } }
  var storageWritable = true;
  function readState() {
    var ls = localStore();
    if (memoryMachines && (!ls || !storageWritable)) return memoryMachines;
    return core.readMachines(ls);
  }
  function writeState(s) {
    memoryMachines = s;
    storageWritable = core.writeMachines(localStore(), s);
  }
  function openDialog(dlg) {
    if (!dlg) return;
    try {
      if (typeof dlg.showModal === 'function') { if (!dlg.open) dlg.showModal(); return; }
    } catch (e) { /* fall through */ }
    dlg.setAttribute('open', '');
  }
  function closeDialog(dlg) {
    if (!dlg) return;
    try {
      if (typeof dlg.close === 'function') { if (dlg.open) dlg.close(); return; }
    } catch (e) { /* fall through */ }
    dlg.removeAttribute('open');
  }
  function templateNode(tpl) {
    if (!tpl) return null;
    var src = tpl.content ? tpl.content.firstElementChild : tpl.firstElementChild;
    return src ? src.cloneNode(true) : null;
  }

  /* Registry fetch: once per URL per page, cached in sessionStorage. */
  function loadRegistry(url) {
    if (!url) return Promise.reject(new Error('no registry url'));
    if (registryPromises[url]) return registryPromises[url];
    var ss = sessionStore();
    try {
      var cached = ss && ss.getItem(REGISTRY_CACHE_KEY);
      if (cached) {
        var parsed = JSON.parse(cached);
        if (parsed && parsed.url === url) {
          var reg0 = core.normalizeRegistry(parsed.data);
          if (reg0) { registryPromises[url] = Promise.resolve(reg0); return registryPromises[url]; }
        }
      }
    } catch (e) { /* ignore cache */ }
    if (typeof window.fetch !== 'function') return Promise.reject(new Error('no fetch'));
    registryPromises[url] = window.fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('registry ' + r.status);
      return r.json();
    }).then(function (data) {
      var reg = core.normalizeRegistry(data);
      if (!reg) throw new Error('registry empty');
      try { if (ss) ss.setItem(REGISTRY_CACHE_KEY, JSON.stringify({ url: url, data: data })); } catch (e) { /* quota */ }
      return reg;
    });
    registryPromises[url].catch(function () { delete registryPromises[url]; });
    return registryPromises[url];
  }

  function parseIsland(root) {
    var el = qs(root, 'script[data-updp-data]');
    var d = {};
    try { d = el ? JSON.parse(el.textContent || '{}') || {} : {}; } catch (e) { d = {}; }
    d.product = d.product && typeof d.product === 'object' ? d.product : {};
    d.fit = Array.isArray(d.fit) ? d.fit : [];
    d.listed_notes = Array.isArray(d.listed_notes) ? d.listed_notes : [];
    d.not_listed_notes = Array.isArray(d.not_listed_notes) ? d.not_listed_notes : [];
    d.addons = Array.isArray(d.addons) ? d.addons : [];
    d.docs = Array.isArray(d.docs) ? d.docs : [];
    d.glance = Array.isArray(d.glance) ? d.glance : [];
    d.settings = d.settings && typeof d.settings === 'object' ? d.settings : {};
    d.strings = d.strings && typeof d.strings === 'object' ? d.strings : {};
    return d;
  }

  /* ------------------------------------------------------------------------------------ */
  function initRoot(root) {
    if (!root || root.__updpReady) return;
    root.__updpReady = true;
    root.setAttribute('data-updp-js', 'on');

    var island = parseIsland(root);
    var str = core.makeStr(island.strings);
    var product = island.product;
    var avail = product.avail || root.getAttribute('data-avail') || 'available';
    var ctx = {
      root: root,
      island: island,
      str: str,
      reg: null,
      regState: 'loading',
      machine: null,
      qty: 1,
      headerH: 0,
      renderers: [],
      bars: null
    };
    root.__updp = ctx;

    function live(msg) {
      var region = qs(root, '[data-updp-live]');
      if (!region) return;
      region.textContent = '';
      setTimeout(function () { region.textContent = msg; }, 40);
    }
    ctx.announce = live;

    function renderAll() {
      ctx.renderers.forEach(function (fn) { try { fn(); } catch (e) { if (window.console) console.error('[updp]', e); } });
    }

    /* No fit list (migration state): Liquid renders no fit row; never compute "Not listed" against an
       empty list, so machine state stays off for this product (L/CONTRACT-ADDITIONS A, fit_available). */
    ctx.fitEnabled = !!qs(root, '[data-updp-fit]') && island.fit_available !== false && island.fit.length > 0;

    /* ---------- page-wide machine state ---------- */
    function applyMachine(detail) {
      var m = null;
      if (detail && detail.id && ctx.fitEnabled) {
        var entry = (ctx.reg && ctx.reg.byId[detail.id]) || {
          id: String(detail.id), name: String(detail.name || ''), catalog_id: String(detail.catalog_id || ''),
          family: String(detail.family || ''), status_hint: detail.status_hint || null, note: '', aliases: []
        };
        var res = core.fitResult(island, entry, str);
        m = { id: entry.id, name: entry.name, catalog_id: entry.catalog_id, family: entry.family, entry: entry, status: res.status, res: res, restored: !!detail.restored };
      }
      ctx.machine = m;
      root.setAttribute('data-machine', m ? m.status : 'none');
      renderAll();
    }
    function dispatchMachine(entry, restored) {
      var detail = entry ? {
        id: entry.id, name: entry.name, catalog_id: entry.catalog_id, family: entry.family,
        status: core.statusFor(entry, island.fit), restored: !!restored
      } : null;
      var ev;
      try { ev = new CustomEvent('updp:machine', { detail: detail }); }
      catch (e) { ev = document.createEvent('CustomEvent'); ev.initCustomEvent('updp:machine', false, false, detail); }
      document.dispatchEvent(ev);
    }
    ctx.dispatchMachine = dispatchMachine;
    document.addEventListener('updp:machine', function (e) {
      if (!root.isConnected) return;
      applyMachine(e.detail || null);
    });

    /* ---------- modules ---------- */
    initHeaderOffset(ctx);
    initQuantity(ctx);
    initCart(ctx);
    initBars(ctx, avail);
    initFit(ctx);
    initOptions(ctx);
    initCompat(ctx);
    initDocs(ctx);
    initAddons(ctx);
    initHelpLinks(ctx);
    initMachineText(ctx);
    initGallery(ctx);
    initSkuCopy(ctx);
    initAccordions(ctx);
    initSectionLinks(ctx);

    root.setAttribute('data-machine', 'none');
    renderAll();

    /* ---------- registry, V1 migration, restore ---------- */
    if (!ctx.fitEnabled) {
      ctx.regState = 'off';
    } else if (island.registry_fail === true) {
      ctx.regState = 'failed';
      renderAll();
    } else {
      loadRegistry(island.registry_url).then(function (reg) {
        ctx.reg = reg;
        ctx.regState = 'ready';
        core.migrateV1(localStore(), reg);
        var st = readState();
        var cur = st.current && reg.byId[st.current];
        if (cur) dispatchMachine(cur, true);
        else renderAll();
      }, function () {
        ctx.regState = 'failed';
        renderAll();
      });
      /* back/forward cache: the machine may have changed on another part page since this one was shown */
      window.addEventListener('pageshow', function (e) {
        if (!e.persisted || !ctx.reg || !root.isConnected) return;
        var st = readState();
        var shown = ctx.machine ? ctx.machine.id : null;
        if ((st.current || null) === shown) return;
        var entry = st.current ? ctx.reg.byId[st.current] : null;
        dispatchMachine(entry || null, !!entry);
      });
    }
  }

  /* ---------- header offset: --updp-header-h on the root ---------- */
  function initHeaderOffset(ctx) {
    var root = ctx.root;
    var sel = ctx.island.settings.header_selector || '.abh-section';
    var observed = null;
    var ro = null;
    function findHeader() {
      var el = null;
      try { el = document.querySelector(sel); } catch (e) { el = null; }
      return el || document.querySelector('[data-abh]') || document.querySelector('header');
    }
    function stickyNode(el) {
      for (var n = el; n && n !== document.documentElement && n !== document.body; n = n.parentElement) {
        var p = '';
        try { p = window.getComputedStyle(n).position; } catch (e) { p = ''; }
        if (p === 'sticky' || p === 'fixed') return n;
      }
      return null;
    }
    function measure() {
      var el = findHeader();
      var s = el ? stickyNode(el) : null;
      var h = s ? Math.max(0, Math.round(s.getBoundingClientRect().height)) : 0;
      ctx.headerH = h;
      root.style.setProperty('--updp-header-h', h + 'px');
      if (s && s !== observed && ro) { if (observed) ro.unobserve(observed); ro.observe(s); observed = s; }
    }
    if (typeof window.ResizeObserver === 'function') ro = new ResizeObserver(function () { measure(); });
    window.addEventListener('resize', measure);
    ctx.measureHeader = measure;
    measure();
  }

  /* ---------- quantity: the main input is the single source of truth ---------- */
  function initQuantity(ctx) {
    var root = ctx.root;
    var str = ctx.str;
    var p = ctx.island.product;
    var input = qs(root, 'input[data-updp-qty]');
    function current() {
      var n = input ? core.clampQty(input.value) : 1;
      return n == null ? ctx.qty || 1 : n;
    }
    function render() {
      var q = ctx.qty;
      var t = core.barTotals(p.price_cents, q, p.currency, str);
      qsa(root, '[data-updp-atc] [data-slot="atc-label"], [data-updp-bar-atc] [data-slot="atc-label"]').forEach(function (el) { setText(el, t.label); });
      qsa(root, '[data-slot="bar-total"]').forEach(function (el) { setText(el, t.total); });
      qsa(root, '[data-slot="bar-each"]').forEach(function (el) { setText(el, t.each); setHidden(el, q <= 1); });
    }
    function update(normalizeInput) {
      var n = current();
      if (normalizeInput && input && String(input.value) !== String(n)) input.value = String(n);
      if (n !== ctx.qty) {
        ctx.qty = n;
        render();
        if (ctx.onQty) ctx.onQty();
      }
    }
    ctx.qty = current();
    if (input) {
      input.addEventListener('input', function () {
        var raw = String(input.value).trim();
        if (raw !== '' && core.clampQty(raw) != null && parseInt(raw, 10) > 99) input.value = '99';
        update(false);
      });
      input.addEventListener('change', function () { update(true); });
      input.addEventListener('blur', function () { update(true); });
    }
    qsa(root, '[data-updp-qty-step]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var step = parseInt(btn.getAttribute('data-updp-qty-step'), 10) || 0;
        var n = core.clampQty(current() + step) || 1;
        if (input) input.value = String(n);
        update(true);
      });
    });
    ctx.renderers.push(render);
  }

  /* ---------- add to cart (main form, section bar, mobile bar, add-on cards) ---------- */
  function initCart(ctx) {
    var root = ctx.root;
    var str = ctx.str;
    var form = qs(root, 'form[data-updp-form]');
    var mainBtn = qs(root, '[data-updp-atc]');

    function variantId() {
      var f = form ? qs(form, 'input[name="id"]') : null;
      return (f && f.value) || ctx.island.product.variant_id;
    }
    function scopeFor(where, trigger) {
      if (where === 'main') return qs(root, '[data-updp-purchase]') || form || root;
      if (where === 'secbar') return trigger.closest('[data-updp-secbar]') || root;
      if (where === 'mbar') return trigger.closest('[data-updp-mbar]') || root;
      return trigger.closest('[data-updp-addon]') || root;
    }
    function errorFor(where, scope) {
      if (where === 'main') return qs(root, '[data-updp-add-error][data-for="main"]');
      return qs(scope, '[data-updp-add-error][data-for="' + where + '"]') || qs(scope, '[data-updp-add-error]');
    }
    function showError(err, msg) {
      if (!err) { ctx.announce(msg); return; }
      var slot = qs(err, '[data-slot="message"]');
      if (slot) setText(slot, msg); else setText(err, msg);
      setHidden(err, false);
    }
    function clearError(err) {
      if (!err) return;
      setHidden(err, true);
      var slot = qs(err, '[data-slot="message"]');
      if (slot) setText(slot, '');
    }
    function showToast(scope, qty, where) {
      var slot = qs(scope, '[data-updp-toast-slot][data-for="' + where + '"]') || qs(scope, '[data-updp-toast-slot]');
      var node = templateNode(qs(root, 'template[data-updp-tpl="toast"]'));
      if (!slot || !node) return;
      while (slot.firstChild) slot.removeChild(slot.firstChild);
      fillSlots(node, 'message', str('added', { n: qty }));
      if (node.getAttribute('data-slot') === 'message') setText(node, str('added', { n: qty }));
      var link = node.matches && node.matches('a[data-updp-toast-cart]') ? node : qs(node, 'a[data-updp-toast-cart]');
      if (link && !link.getAttribute('href')) link.setAttribute('href', '/cart');
      slot.appendChild(node);
      var t = setTimeout(function () { if (node.parentNode && !node.contains(document.activeElement)) node.parentNode.removeChild(node); }, 8000);
      node.addEventListener('focusout', function () {
        clearTimeout(t);
        t = setTimeout(function () { if (node.parentNode && !node.contains(document.activeElement)) node.parentNode.removeChild(node); }, 4000);
      });
    }
    function refreshCount() {
      if (typeof window.fetch !== 'function') return;
      window.fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) { qsa(document, '.cart-count, .cart-flyout-cart-text').forEach(function (el) { el.textContent = String(c.item_count); }); })
        .catch(function () { /* count stays */ });
    }
    function afterAdd(key, qty, scope, where) {
      if (typeof window.openFlyoutCart === 'function') {
        try {
          var p = window.openFlyoutCart({ addedKey: key });
          if (p && typeof p.then === 'function') p.then(null, function () { showToast(scope, qty, where); refreshCount(); });
          return 'drawer';
        } catch (e) { /* fall back */ }
      }
      var trig = document.querySelector('.flyout-trigger');
      if (trig) { trig.click(); return 'trigger'; }
      showToast(scope, qty, where);
      refreshCount();
      return 'toast';
    }
    function add(trigger, where, id, qty) {
      if (!trigger || trigger.__updpBusy) return Promise.resolve(false);
      trigger.__updpBusy = true;
      trigger.setAttribute('aria-busy', 'true');
      trigger.setAttribute('data-busy', '');
      var scope = scopeFor(where, trigger);
      var err = errorFor(where, scope);
      clearError(err);
      var vid = /^\d+$/.test(String(id)) ? Number(id) : id;
      var done = function () {
        trigger.__updpBusy = false;
        trigger.removeAttribute('aria-busy');
        trigger.removeAttribute('data-busy');
      };
      if (typeof window.fetch !== 'function') { done(); return Promise.resolve(false); }
      return window.fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: vid, quantity: qty }] })
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (data) { return { ok: r.ok, data: data }; });
      }).then(function (res) {
        if (!res.ok) {
          var d = res.data || {};
          var msg = typeof d.description === 'string' && d.description ? d.description : '';
          throw { updpMessage: msg };
        }
        var data = res.data || {};
        var item = (Array.isArray(data.items) && data.items[0]) || data;
        ctx.announce(str('added_live', { n: qty }));
        afterAdd(item && item.key, qty, scope, where);
        done();
        return true;
      }).catch(function (e) {
        showError(err, e && e.updpMessage ? e.updpMessage : str('add_failed'));
        done();
        return false;
      });
    }
    ctx.addToCart = add;

    if (form && mainBtn) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        add(mainBtn, 'main', variantId(), ctx.qty);
      });
    }
    qsa(root, '[data-updp-bar-atc]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var where = btn.closest('[data-updp-mbar]') ? 'mbar' : 'secbar';
        add(btn, where, variantId(), ctx.qty);
      });
    });
    qsa(root, '[data-updp-addon-add]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var card = btn.closest('[data-updp-addon]');
        var id = btn.getAttribute('data-variant-id') || (card && card.getAttribute('data-variant-id'));
        if (id) add(btn, 'addon', id, 1);
      });
    });
  }

  /* ---------- sticky section bar + mobile purchase bar ---------- */
  function initBars(ctx, avail) {
    var root = ctx.root;
    var secbar = qs(root, '[data-updp-secbar]');
    var buy = qs(secbar, '[data-updp-secbar-buy]');
    var mbar = qs(root, '[data-updp-mbar]');
    var purchase = qs(root, '[data-updp-purchase]');
    var atc = qs(root, '[data-updp-atc]');
    var unavailable = avail === 'unavailable' || !atc;
    var st = ctx.bars = {
      purchaseOut: false, atcOut: false, footerVisible: false, keyboard: false,
      drawerOpen: false, menuOpen: false, sheetOpen: false, lightboxOpen: false
    };

    function update() {
      var overlay = st.sheetOpen || st.lightboxOpen || st.drawerOpen || st.menuOpen;
      var showBuy = !!buy && st.purchaseOut && !overlay && !st.keyboard && !unavailable;
      if (buy) setHidden(buy, !showBuy);
      if (secbar) setAttr(secbar, 'data-stuck', showBuy);
      var showM = !!mbar && !unavailable && isMobile() && st.atcOut && !st.footerVisible && !overlay && !st.keyboard;
      if (mbar) setHidden(mbar, !showM);
      root.setAttribute('data-mbar', showM ? 'on' : 'off');
    }
    ctx.updateBars = update;
    /* chat bubble clearance: publish the pinned bar's height (CSS lifts the Shopify chat bubble by it) */
    if (mbar && typeof window.ResizeObserver === 'function') {
      new ResizeObserver(function () { document.documentElement.style.setProperty('--updp-mbar-h', mbar.offsetHeight + 'px'); }).observe(mbar);
    }

    function renderFit() {
      var m = ctx.machine;
      [secbar, mbar].forEach(function (bar) {
        if (!bar) return;
        var which = bar === mbar ? 'mbar' : 'secbar';
        var fitEl = qs(bar, '[data-updp-bar-fit]');
        if (fitEl) {
          if (m) {
            fitEl.setAttribute('data-status', m.status);
            var slot = qs(fitEl, '[data-slot="text"]');
            if (slot) setText(slot, m.res.bar); else setText(fitEl, m.res.bar);
          }
          setHidden(fitEl, !m);
        }
        setHidden(qs(bar, '[data-updp-bar-avail]'), !core.barAvailVisible(which, !!m, avail));
      });
    }
    ctx.renderers.push(renderFit);

    /* Visibility is decided from geometry, not from IntersectionObserver transitions: a jump from below the
       fold straight past the button (in-page link, hash URL, scroll restoration) is out→out and fires no IO
       callback (QA A8). measure() reads the rects; IO callbacks are only a fast trigger, and a passive
       scroll/resize listener (rAF-throttled) plus init, load and pageshow cover everything else. */
    var footer = document.querySelector(ctx.island.settings.footer_selector || 'footer.abf') || lastFooter(root);
    function measure() {
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      var top = ctx.headerH || 0;
      if (purchase) st.purchaseOut = core.outOfView(purchase.getBoundingClientRect(), top);
      if (atc) st.atcOut = core.outOfView(atc.getBoundingClientRect(), top);
      if (footer) st.footerVisible = core.footerNear(footer.getBoundingClientRect(), vh, FOOTER_MARGIN);
      update();
    }
    ctx.measureBars = measure;
    var queued = false;
    function schedule() {
      if (queued) return;
      queued = true;
      raf(function () { queued = false; measure(); });
    }
    if (typeof window.IntersectionObserver === 'function') {
      var io = new IntersectionObserver(function () { measure(); }, { threshold: [0, 1], rootMargin: '0px 0px ' + FOOTER_MARGIN + 'px 0px' });
      [purchase, atc, footer].forEach(function (el) { if (el) io.observe(el); });
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
    window.addEventListener('pageshow', schedule);
    measure();

    /* keyboard up */
    var vv = window.visualViewport;
    if (vv && vv.addEventListener) {
      /* keyboard: the visual viewport shrinks below 75% of the layout viewport (× scale, so pinch-zoom doesn't count) */
      var kb = function () { st.keyboard = vv.height * (vv.scale || 1) < 0.75 * window.innerHeight; update(); };
      vv.addEventListener('resize', kb);
      kb();
    }

    /* cart drawer (Cart V2) and V3 menu drawer */
    function watchAttr(el, attr, fn) {
      if (!el) return false;
      fn();
      if (typeof window.MutationObserver === 'function') {
        new MutationObserver(function () { fn(); update(); }).observe(el, { attributes: true, attributeFilter: [attr] });
      }
      return true;
    }
    function hookDrawer() {
      var d = document.querySelector('dialog#abcart-drawer');
      return watchAttr(d, 'open', function () { st.drawerOpen = d.hasAttribute('open'); });
    }
    if (!hookDrawer()) window.addEventListener('load', function () { hookDrawer(); update(); });
    var menu = document.querySelector('[data-abh-drawer]');
    watchAttr(menu, 'hidden', function () { st.menuOpen = !menu.hasAttribute('hidden'); });

    onMqChange(mobileMq, update);
    update();
  }
  function lastFooter(root) {
    var all = qsa(document, 'footer').filter(function (f) { return !root.contains(f); });
    return all.length ? all[all.length - 1] : null;
  }

  /* ---------- fit row: combobox, desktop panel, mobile sheet, saved machines ---------- */
  function initFit(ctx) {
    var root = ctx.root;
    var str = ctx.str;
    var island = ctx.island;
    if (!ctx.fitEnabled) return;
    var fit = qs(root, '[data-updp-fit]');
    var openBtn = qs(fit, '[data-updp-fit-open]');
    var panel = qs(fit, '[data-updp-fit-panel]');
    var inner = qs(root, '[data-updp-fit-panel-inner]');
    var combo = qs(inner, 'input[data-updp-combo]');
    var comboClear = qs(inner, '[data-updp-combo-clear]');
    var saved = qs(inner, '[data-updp-saved]');
    var savedList = qs(inner, '[data-updp-saved-list]');
    var clearMachine = qs(inner, '[data-updp-clear-machine]');
    var listwrap = qs(inner, '[data-updp-listwrap]');
    var listbox = qs(inner, '[data-updp-listbox]');
    var notfound = qs(inner, '[data-updp-notfound]');
    var links = qs(inner, '[data-updp-fit-links]');
    var sheetStatus = qs(inner, '[data-updp-sheet-status]');
    var result = qs(fit, '[data-updp-fit-result]');
    var changeBtn = qs(result, '[data-updp-fit-change]');
    var unavailableBox = qs(fit, '[data-updp-fit-unavailable]');
    var sheet = qs(root, 'dialog[data-updp-sheet]');
    var sheetBody = qs(sheet, '[data-updp-sheet-body]');
    var tplOption = qs(root, 'template[data-updp-tpl="option"]');
    var tplChip = qs(root, 'template[data-updp-tpl="saved-chip"]');
    var innerHome = inner ? inner.parentNode : null;
    var innerMarker = null;
    if (inner && innerHome) { innerMarker = document.createComment('updp-fit-panel-inner'); innerHome.insertBefore(innerMarker, inner); }
    var listId = (listbox && listbox.id) || 'updp-listbox';
    if (listbox && !listbox.id) listbox.id = listId;

    var f = { open: false, sheet: false, listOpen: false, active: -1, results: [], opener: null, escClosedList: false };
    ctx.fit = f;

    function state() {
      if (ctx.regState === 'failed') return 'unavailable';
      if (f.open) return 'open';
      return ctx.machine ? 'chosen' : 'empty';
    }
    function query() { return combo ? String(combo.value || '') : ''; }

    function renderRow() {
      if (!fit) return;
      var s = state();
      fit.setAttribute('data-state', s);
      setHidden(openBtn, s !== 'empty');
      if (openBtn) openBtn.setAttribute('aria-expanded', f.open || f.sheet ? 'true' : 'false');
      if (changeBtn) changeBtn.setAttribute('aria-expanded', f.open || f.sheet ? 'true' : 'false');
      setHidden(panel, s !== 'open');
      setHidden(unavailableBox, s !== 'unavailable');
      var m = ctx.machine;
      setHidden(result, s !== 'chosen');
      if (!result) return;
      if (!m) { result.removeAttribute('data-status'); return; }
      var res = m.res;
      result.setAttribute('data-status', m.status);
      fillSlots(result, 'model', m.name);
      qsa(result, '[data-slot="restored"]').forEach(function (el) {
        if (!String(el.textContent || '').trim()) setText(el, core.leadSpace(str('from_last_visit')));
        setHidden(el, !m.restored);
      });
      fillSlots(result, 'title', res.title);
      fillSlots(result, 'evidence', res.evidence);
      /* By default the condition is appended to the evidence line (README → Fit phrases), so the separate
         condition element stays hidden (slot still filled); settings.fit_condition_separate flips it. */
      var cond = qs(result, '[data-updp-fit-cond]');
      fillSlots(cond, 'condition', res.condition);
      setHidden(cond, !res.conditionSeparate);
      var note = qs(result, '[data-updp-fit-note]');
      fillSlots(note, 'note', res.note);
      setHidden(note, !res.note);
      var alt = qs(result, 'a[data-updp-fit-alt]');
      if (alt) {
        if (res.alt) {
          alt.setAttribute('href', res.alt.url);
          fillSlots(alt, 'alt-sku', res.alt.sku);
          fillSlots(alt, 'text', str('fit_view_sku', { sku: res.alt.sku }));
        }
        setHidden(alt, !res.alt);
      }
      setHidden(qs(result, 'a[data-updp-see-list]'), m.status !== 'listed');
      qsa(result, 'a[data-updp-help="confirm-fit"]').forEach(function (a) { setHidden(a, m.status === 'listed'); });
    }

    function savedEntries() {
      if (!ctx.reg) return [];
      return readState().recent.map(function (id) { return ctx.reg.byId[id]; }).filter(Boolean);
    }

    function buildOption(entry, i) {
      var li = templateNode(tplOption) || document.createElement('li');
      var status = core.statusFor(entry, island.fit);
      li.id = listId + '-opt-' + i;
      li.setAttribute('role', 'option');
      li.setAttribute('data-status', status);
      li.setAttribute('data-index', String(i));
      li.setAttribute('data-registry-id', entry.id);
      li.setAttribute('aria-selected', 'false');
      var tile = qs(li, '[data-slot="tile"]');
      if (tile) {
        var img = tile.tagName === 'IMG' ? tile : qs(tile, 'img');
        if (entry.family_image && img) { img.setAttribute('src', entry.family_image); img.setAttribute('alt', ''); }
        else if (img) img.parentNode.removeChild(img);
      }
      fillSlots(li, 'name', entry.name);
      fillSlots(li, 'family', entry.family);
      fillSlots(li, 'status', core.pillText(status, core.brandOf(island) === 'abi', str));
      if (!qs(li, '[data-slot="name"]')) setText(li, entry.name);
      return li;
    }

    function listShown() { return !!(listwrap && !listwrap.hasAttribute('hidden')); }

    function updateActive() {
      if (!listbox || !combo) return;
      var opts = qsa(listbox, '[role="option"]');
      opts.forEach(function (li, i) { li.setAttribute('aria-selected', i === f.active ? 'true' : 'false'); });
      var cur = f.active >= 0 ? opts[f.active] : null;
      if (cur && listShown()) {
        combo.setAttribute('aria-activedescendant', cur.id);
        if (cur.scrollIntoView) { try { cur.scrollIntoView({ block: 'nearest' }); } catch (e) { /* old engines */ } }
      } else {
        combo.removeAttribute('aria-activedescendant');
      }
    }

    function renderPanel() {
      if (!inner) return;
      var q = query();
      var nq = core.norm(q);
      var typing = q.length > 0;
      var enough = nq.length >= 2;
      f.results = ctx.reg ? core.rank(ctx.reg, q, 7) : [];
      if (f.active >= f.results.length) f.active = -1;
      if (listbox) {
        while (listbox.firstChild) listbox.removeChild(listbox.firstChild);
        f.results.forEach(function (r, i) { listbox.appendChild(buildOption(r.entry, i)); });
      }
      var showList = f.listOpen && enough && f.results.length > 0;
      setHidden(listwrap, !showList);
      if (combo) combo.setAttribute('aria-expanded', showList ? 'true' : 'false');
      var nf = enough && ctx.regState === 'ready' && f.results.length === 0;
      if (notfound) { fillSlots(notfound, 'query', q.trim()); setHidden(notfound, !nf); }
      updateActive();

      var entries = savedEntries();
      var cur = ctx.machine ? ctx.machine.id : null;
      if (savedList) {
        while (savedList.firstChild) savedList.removeChild(savedList.firstChild);
        entries.forEach(function (e) {
          var chip = templateNode(tplChip) || document.createElement('button');
          if (chip.tagName === 'BUTTON' && !chip.getAttribute('type')) chip.setAttribute('type', 'button');
          chip.setAttribute('data-registry-id', e.id);
          chip.setAttribute('aria-pressed', e.id === cur ? 'true' : 'false');
          if (qs(chip, '[data-slot="name"]')) fillSlots(chip, 'name', e.name); else setText(chip, e.name);
          savedList.appendChild(chip);
        });
      }
      setHidden(saved, typing || (!entries.length && !ctx.machine));
      setHidden(clearMachine, !ctx.machine);
      setHidden(links, typing);
      setHidden(comboClear, !typing);
      if (sheetStatus) setText(sheetStatus, f.sheet && enough && ctx.regState === 'ready' ? core.sheetMatchesText(f.results.length, q.trim(), str) : '');
      var title = str(ctx.machine ? 'panel_change' : 'panel_check');
      fillSlots(inner, 'panel-title', title);
      if (sheet) fillSlots(sheet, 'panel-title', title);
    }
    ctx.renderers.push(function () { renderRow(); renderPanel(); });

    function openPanel(opener) {
      if (!fit || !inner || ctx.regState === 'failed') {
        if (fit && fit.scrollIntoView) { try { fit.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' }); } catch (e) { /* ok */ } }
        return;
      }
      f.opener = opener || document.activeElement;
      if (combo) combo.value = '';
      f.listOpen = false;
      f.active = -1;
      if (isMobile() && sheet && sheetBody) {
        sheetBody.appendChild(inner);
        if (combo && combo.getAttribute('data-placeholder-mobile')) {
          if (combo.__updpPh == null) combo.__updpPh = combo.getAttribute('placeholder') || '';
          combo.setAttribute('placeholder', combo.getAttribute('data-placeholder-mobile'));
        }
        f.sheet = true;
        f.open = false;
        openDialog(sheet);
        if (ctx.bars) { ctx.bars.sheetOpen = true; ctx.updateBars(); }
      } else {
        f.open = true;
      }
      renderRow();
      renderPanel();
      /* focus after the sheet/panel is shown; skipped if it was closed again in the meantime */
      if (combo) setTimeout(function () { if (f.open || f.sheet) focusEl(combo); }, 0);
    }
    ctx.openFit = openPanel;

    function afterSheetClosed() {
      if (combo && combo.__updpPh != null) combo.setAttribute('placeholder', combo.__updpPh);
      if (inner && innerHome) {
        if (innerMarker && innerMarker.parentNode === innerHome) innerHome.insertBefore(inner, innerMarker.nextSibling);
        else innerHome.appendChild(inner);
      }
      if (ctx.bars) { ctx.bars.sheetOpen = false; ctx.updateBars(); }
    }
    function pickFocus(target) {
      if (target && rendered(target)) return target;
      if (f.opener && f.opener !== combo && rendered(f.opener) && (!inner || !inner.contains(f.opener))) return f.opener;
      return ctx.machine ? changeBtn : openBtn;
    }
    function closePanel(focusTarget) {
      var wasSheet = f.sheet;
      f.open = false;
      f.listOpen = false;
      f.active = -1;
      if (combo) combo.value = '';
      if (wasSheet) {
        f.sheet = false;
        closeDialog(sheet);
        afterSheetClosed();
      }
      renderRow();
      renderPanel();
      var t = pickFocus(focusTarget);
      f.opener = null;
      if (t) focusEl(t);
    }
    if (sheet) {
      sheet.addEventListener('close', function () {
        if (!f.sheet) return;
        f.sheet = false;
        afterSheetClosed();
        if (combo) combo.value = '';
        f.listOpen = false;
        renderRow();
        renderPanel();
        var t = pickFocus(null);
        f.opener = null;
        if (t) focusEl(t);
      });
      sheet.addEventListener('cancel', function (e) {
        if (f.escClosedList) { e.preventDefault(); f.escClosedList = false; }
      });
      sheet.addEventListener('click', function (e) { if (e.target === sheet) closePanel(); });
      qsa(sheet, '[data-updp-sheet-close]').forEach(function (b) {
        b.addEventListener('click', function (e) { e.preventDefault(); closePanel(); });
      });
    }
    onMqChange(mobileMq, function () { if (f.sheet || f.open) closePanel(); });

    function choose(entry) {
      if (!entry) return;
      writeState(core.chooseMachine(readState(), entry.id));
      ctx.dispatchMachine(entry, false);
      closePanel(changeBtn);
    }
    function clearSelection() {
      writeState(core.clearCurrent(readState()));
      ctx.dispatchMachine(null, false);
      closePanel(openBtn);
    }

    if (openBtn) openBtn.addEventListener('click', function (e) { e.preventDefault(); openPanel(openBtn); });
    if (changeBtn) changeBtn.addEventListener('click', function (e) { e.preventDefault(); openPanel(changeBtn); });
    qsa(inner, '[data-updp-fit-close]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); closePanel(); });
    });
    if (clearMachine) clearMachine.addEventListener('click', function (e) { e.preventDefault(); clearSelection(); });
    if (savedList) {
      savedList.addEventListener('click', function (e) {
        var chip = e.target.closest && e.target.closest('[data-registry-id]');
        if (!chip || !ctx.reg) return;
        e.preventDefault();
        choose(ctx.reg.byId[chip.getAttribute('data-registry-id')]);
      });
    }
    if (comboClear) {
      comboClear.addEventListener('click', function (e) {
        e.preventDefault();
        if (combo) combo.value = '';
        f.listOpen = false;
        f.active = -1;
        renderPanel();
        focusEl(combo);
      });
    }
    if (listbox) {
      listbox.addEventListener('mousedown', function (e) { e.preventDefault(); });
      listbox.addEventListener('click', function (e) {
        var li = e.target.closest && e.target.closest('[role="option"]');
        if (!li) return;
        var i = parseInt(li.getAttribute('data-index'), 10);
        if (f.results[i]) choose(f.results[i].entry);
      });
      var hover = function (e) {
        var li = e.target.closest && e.target.closest('[role="option"]');
        if (!li) return;
        var i = parseInt(li.getAttribute('data-index'), 10);
        if (i !== f.active && !isNaN(i)) { f.active = i; updateActive(); }
      };
      listbox.addEventListener('mousemove', hover);
      listbox.addEventListener('mouseover', hover);
    }
    if (combo) {
      combo.addEventListener('input', function () {
        f.listOpen = true;
        f.active = -1;
        renderPanel();
      });
      combo.addEventListener('focus', function () {
        if (core.norm(query()).length >= 2 && !f.listOpen) { f.listOpen = true; renderPanel(); }
      });
      combo.addEventListener('blur', function () {
        setTimeout(function () {
          if (document.activeElement === combo) return;
          if (f.listOpen) { f.listOpen = false; renderPanel(); }
        }, 0);
      });
      combo.addEventListener('keydown', function (e) {
        var n = f.results.length;
        var k = e.key;
        if (k === 'ArrowDown' || k === 'Down') {
          e.preventDefault();
          if (!listShown()) { f.listOpen = true; f.active = n ? 0 : -1; renderPanel(); return; }
          f.active = f.active < 0 ? 0 : Math.min(f.active + 1, n - 1);
          updateActive();
        } else if (k === 'ArrowUp' || k === 'Up') {
          e.preventDefault();
          if (!listShown()) { f.listOpen = true; f.active = n ? n - 1 : -1; renderPanel(); return; }
          f.active = f.active < 0 ? n - 1 : Math.max(f.active - 1, 0);
          updateActive();
        } else if (k === 'Enter') {
          e.preventDefault(); /* the fit row can sit inside the product form: never submit it */
          if (listShown() && f.active >= 0 && f.results[f.active]) { choose(f.results[f.active].entry); return; }
          var exact = core.uniqueExact(f.results);
          if (exact && core.norm(query()).length >= 2) choose(exact);
        } else if (k === 'Escape' || k === 'Esc') {
          if (listShown()) {
            e.preventDefault();
            f.escClosedList = f.sheet;
            f.listOpen = false;
            f.active = -1;
            renderPanel();
            setTimeout(function () { f.escClosedList = false; }, 0);
            return;
          }
          if (f.sheet) { e.preventDefault(); closePanel(); return; }
          e.preventDefault();
          if (query()) { combo.value = ''; f.active = -1; renderPanel(); return; }
          closePanel();
        } else if (k === 'Tab') {
          if (f.listOpen) { f.listOpen = false; f.active = -1; renderPanel(); }
        }
      });
    }
    /* Esc anywhere in the desktop panel closes it */
    if (panel) {
      panel.addEventListener('keydown', function (e) {
        if ((e.key === 'Escape' || e.key === 'Esc') && e.target !== combo && f.open) { e.preventDefault(); closePanel(); }
      });
    }

    /* other "Check your machine" triggers (options module link, add-on prompt card, any #updp-fit-panel link) */
    root.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('a[data-updp-options-check], [data-updp-open-fit], a[href="#updp-fit-panel"]');
      if (!t || !root.contains(t)) return;
      e.preventDefault();
      if (!isMobile() && fit && fit.scrollIntoView) {
        try { fit.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' }); } catch (err) { /* ok */ }
      }
      openPanel(t);
    });

  }

  /* ---------- OEM and ABI options + ABI "Replaces" line ---------- */
  function initOptions(ctx) {
    var root = ctx.root;
    var str = ctx.str;
    var eq = ctx.island.equivalence;
    var options = qs(root, '[data-updp-options]');
    var replaces = qs(root, '[data-updp-replaces]');
    var note = qs(root, '[data-updp-options-note]');
    /* text after the SKU link ("." in the copy-deck sentence): [data-slot="note-after"] when the markup has it,
       else one text node J owns right after the link (textContent only) */
    function noteAfter(box, link, text) {
      var slot = qs(box, '[data-slot="note-after"]');
      if (slot) { setText(slot, text); return; }
      var tn = link.__updpAfter;
      if (!tn) {
        tn = document.createTextNode('');
        link.parentNode.insertBefore(tn, link.nextSibling);
        link.__updpAfter = tn;
      }
      tn.data = text;
    }
    function render() {
      var m = ctx.machine;
      var c = m ? m.catalog_id : null;
      var scope = core.scopeState(eq, c);
      qsa(options, '[data-updp-scope]').forEach(function (el) {
        el.setAttribute('data-state', scope);
        if (m) fillSlots(el, 'model', m.name);
      });
      var listedSomewhere = false;
      qsa(options, '[data-updp-opt-card]').forEach(function (card) {
        var brand = card.getAttribute('data-brand') === 'abi' ? 'abi' : 'oem';
        var status = core.optionFit(eq, brand, c);
        if (status === 'listed') listedSomewhere = true;
        qsa(card, '[data-updp-opt-fit]').forEach(function (el) {
          el.setAttribute('data-status', status);
          if (m) fillSlots(el, 'model', m.name);
          var t = qs(el, '[data-slot="text"]');
          if (t) setText(t, core.optionFitText(brand, status, m ? m.name : '', str));
        });
      });
      if (replaces) {
        replaces.setAttribute('data-state', scope);
        if (m) fillSlots(replaces, 'model', m.name);
        setHidden(replaces, scope === 'out');
      }
      if (note) {
        var p = m && !listedSomewhere && options ? core.findNote(ctx.island.not_listed_notes, c) : null;
        if (p) {
          var parts = core.optionsNoteParts(ctx.island, p, m.name, str);
          var link = qs(note, 'a[data-updp-options-note-link]');
          var linked = !!(link && parts.sku);
          fillSlots(note, 'note', linked ? parts.before : parts.full);
          if (link) {
            if (linked) link.setAttribute('href', parts.href);
            /* cleared when there's no SKU, so a hidden link never carries a stale one */
            if (qs(link, '[data-slot="alt-sku"]')) fillSlots(link, 'alt-sku', linked ? parts.sku : ''); else setText(link, linked ? parts.sku : '');
            setHidden(link, !linked);
            noteAfter(note, link, linked ? parts.after : '');
          }
        }
        setHidden(note, !p);
      }
    }
    ctx.renderers.push(render);
  }

  /* ---------- Compatibility: pin, mark, collapse, strip, search, + more ---------- */
  function initCompat(ctx) {
    var root = ctx.root;
    var str = ctx.str;
    var compat = qs(root, '[data-updp-compat]');
    if (!compat) { ctx.renderers.push(function () { renderAccSummary(ctx, null); }); return; }
    var list = qs(compat, '[data-updp-compat-list]');
    var families = qsa(list || compat, '[data-updp-family]');
    var strip = qs(compat, '[data-updp-compat-strip]');
    var search = qs(compat, 'input[data-updp-compat-search]');
    var clearBtn = qs(compat, '[data-updp-compat-clear]');
    var status = qs(compat, '[data-updp-compat-status]');
    var nomatch = qs(compat, '[data-updp-compat-nomatch]');
    var expanded = [];   /* families opened with "+ k more" */
    var shown = [];      /* collapsed families opened with "Show models" */
    var q = '';
    var timer = null;
    var lastPinned = null;
    var orderedFor = null;
    var famMarkers = families.map(function (fam) {
      var mk = document.createComment('updp-family');
      fam.parentNode.insertBefore(mk, fam);
      return mk;
    });

    function has(listArr, el) { return listArr.indexOf(el) !== -1; }
    /* Move the pinned family first; put it back when the pin changes. Only moves nodes when the pin
       changes, so focus inside a family survives other re-renders. */
    function order(pinned) {
      if (pinned === orderedFor) return;
      if (orderedFor) {
        var i = families.indexOf(orderedFor);
        var mk = famMarkers[i];
        if (mk && mk.parentNode) mk.parentNode.insertBefore(orderedFor, mk.nextSibling);
      }
      if (pinned) {
        var box = list || pinned.parentNode;
        var first = famMarkers[0];
        box.insertBefore(pinned, first && first.parentNode === box ? first : box.firstChild);
      }
      orderedFor = pinned;
    }
    function yoursChip(m) {
      if (!m || m.status !== 'listed') return null;
      var chips = qsa(compat, '[data-updp-chip]');
      for (var i = 0; i < chips.length; i++) if (chips[i].getAttribute('data-registry-id') === m.id) return chips[i];
      for (var j = 0; j < chips.length; j++) {
        if (chips[j].getAttribute('data-catalog-id') === m.catalog_id && core.norm(chips[j].getAttribute('data-name')) === core.norm(m.name)) return chips[j];
      }
      return null;
    }
    function aliasesFor(chip) {
      var id = chip.getAttribute('data-registry-id');
      var e = id && ctx.reg ? ctx.reg.byId[id] : null;
      return e ? e.aliases : [];
    }

    function render() {
      var m = ctx.machine;
      var mine = yoursChip(m);
      var pinned = mine ? mine.closest('[data-updp-family]') : null;
      if (pinned !== lastPinned) { shown = []; lastPinned = pinned; }
      var searching = core.norm(q).length >= 2;
      var nq = core.norm(q);
      var total = 0;

      order(pinned);

      families.forEach(function (fam) {
        var isPinned = fam === pinned;
        setAttr(fam, 'data-pinned', isPinned);
        var inc = qs(fam, '[data-updp-includes]');
        if (inc) { if (isPinned) fillSlots(inc, 'model', m.name); setHidden(inc, !isPinned); }
        var collapsed = !!pinned && !isPinned && !has(shown, fam) && !searching;
        setAttr(fam, 'data-collapsed', collapsed);
        var sm = qs(fam, '[data-updp-show-models]');
        if (sm) {
          setHidden(sm, !(pinned && !isPinned) || searching);
          sm.setAttribute('aria-expanded', has(shown, fam) ? 'true' : 'false');
        }
        var chips = qsa(fam, '[data-updp-chip]');
        var famMatch = 0;
        var extraMatch = false;
        chips.forEach(function (chip) {
          setAttr(chip, 'data-yours', chip === mine);
          var hit = searching && core.chipMatches(chip.getAttribute('data-name') || chip.textContent, aliasesFor(chip), nq);
          setAttr(chip, 'data-match', hit);
          if (hit) { famMatch++; if (chip.hasAttribute('data-extra')) extraMatch = true; }
        });
        total += famMatch;
        setHidden(fam, searching && famMatch === 0);
        var extras = chips.filter(function (c) { return c.hasAttribute('data-extra'); });
        var forced = (isPinned && mine && mine.hasAttribute('data-extra')) || (searching && extraMatch);
        var open = has(expanded, fam) || forced;
        extras.forEach(function (c) { setHidden(c, !open); });
        var more = qs(fam, '[data-updp-more]');
        if (more) {
          more.setAttribute('aria-expanded', open ? 'true' : 'false');
          setHidden(more, !extras.length || open);
        }
      });

      if (strip) {
        if (m) {
          var listed = m.status === 'listed';
          strip.setAttribute('data-state', listed ? 'listed' : 'notlisted');
          fillSlots(strip, 'model', m.name);
          fillSlots(strip, 'family', m.family);
          fillSlots(strip, 'note', listed ? '' : m.res.note);
        }
        setHidden(strip, !m);
      }
      if (status) setText(status, searching ? core.matchesText(total, str) : '');
      if (nomatch) fillSlots(nomatch, 'query', String(q).trim());
      setHidden(nomatch, !(searching && total === 0));
      setHidden(clearBtn, !(search && search.value));
      renderAccSummary(ctx, m);
    }
    ctx.renderers.push(render);

    compat.addEventListener('click', function (e) {
      var more = e.target.closest && e.target.closest('[data-updp-more]');
      if (more) {
        e.preventDefault();
        var fam = more.closest('[data-updp-family]');
        if (!fam) return;
        if (!has(expanded, fam)) expanded.push(fam);
        var firstHidden = qsa(fam, '[data-updp-chip][data-extra]').filter(function (c) { return c.hasAttribute('hidden'); })[0];
        render();
        if (firstHidden) {
          if (!firstHidden.hasAttribute('tabindex')) firstHidden.setAttribute('tabindex', '-1');
          focusEl(firstHidden);
        }
        return;
      }
      var sm = e.target.closest && e.target.closest('[data-updp-show-models]');
      if (sm) {
        e.preventDefault();
        var f2 = sm.closest('[data-updp-family]');
        if (!f2) return;
        var opening = !has(shown, f2);
        if (opening) shown.push(f2);
        else shown = shown.filter(function (x) { return x !== f2; });
        render();
        /* CSS may hide "Show models" once the family is open; keep focus inside the family */
        if (opening && !rendered(sm)) {
          var first = qsa(f2, '[data-updp-chip]').filter(function (c) { return !c.hasAttribute('hidden'); })[0];
          if (first) {
            if (!first.hasAttribute('tabindex')) first.setAttribute('tabindex', '-1');
            focusEl(first);
          }
        }
      }
    });

    if (search) {
      var run = function () { q = search.value || ''; render(); };
      search.addEventListener('input', function () {
        clearTimeout(timer);
        setHidden(clearBtn, !search.value);
        timer = setTimeout(run, 150);
      });
      search.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); clearTimeout(timer); run(); }
        else if ((e.key === 'Escape' || e.key === 'Esc') && search.value) { e.preventDefault(); search.value = ''; clearTimeout(timer); run(); }
      });
    }
    if (clearBtn) {
      clearBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (search) search.value = '';
        clearTimeout(timer);
        q = '';
        render();
        focusEl(search);
      });
    }
  }
  function renderAccSummary(ctx, m) {
    var t = compatToggle(ctx);
    if (!t) return;
    var slot = qs(t, '[data-slot="acc-summary"]');
    if (!slot) return;
    if (slot.__updpOriginal == null) slot.__updpOriginal = slot.textContent;
    if (!m) setText(slot, ctx.island.strings.acc_compat_none ? ctx.str('acc_compat_none') : (slot.__updpOriginal || ctx.str('acc_compat_none')));
    else setText(slot, ctx.str(core.accSummaryKey(m.status), { model: m.name }));
  }
  function compatToggle(ctx) {
    var toggles = qsa(ctx.root, '[data-updp-acc-toggle]');
    var byValue = toggles.filter(function (t) { return t.getAttribute('data-updp-acc-toggle') === 'compatibility'; })[0];
    if (byValue) return byValue;
    var compat = qs(ctx.root, '[data-updp-compat]');
    var sec = document.getElementById('compatibility');
    for (var i = 0; i < toggles.length; i++) {
      var p = document.getElementById(toggles[i].getAttribute('aria-controls') || '');
      if (!p) continue;
      if ((compat && (p.contains(compat) || compat.contains(p))) || (sec && (sec.contains(p) || p.contains(sec)))) return toggles[i];
    }
    return null;
  }

  /* ---------- Installation & downloads ---------- */
  function initDocs(ctx) {
    var root = ctx.root;
    var str = ctx.str;
    var docs = qs(root, '[data-updp-docs]');
    if (!docs) return;
    var cards = qsa(docs, '[data-updp-doc]');
    var glances = qsa(docs, '[data-updp-glance]');
    var baseGroups = qsa(docs, '[data-updp-docgroup="part"], [data-updp-docgroup="care"], [data-updp-docgroup="manuals"]');
    var baseHidden = baseGroups.map(function (g) { return g.hasAttribute('hidden'); });
    var mineGroup = qs(docs, '[data-updp-docgroup="mine"]');
    var manualGroup = qs(docs, '[data-updp-docgroup="mymanual"]');
    var othersWrap = qs(docs, '[data-updp-docs-others]');
    var othersToggle = qs(othersWrap, '[data-updp-docs-others-toggle]');
    var othersPanel = qs(othersWrap, '[data-updp-docs-others-panel]');
    var empty = qs(docs, '[data-updp-docs-empty]');
    /* no-machine-only rows (L: [data-updp-docs-none], Liquid-shown): hidden while a machine is chosen */
    var noMachineOnly = qsa(docs, '[data-updp-docs-none], [data-updp-docs-nomachine]');
    var noMachineHidden = noMachineOnly.map(function (el) { return el.hasAttribute('hidden'); });
    var markers = new Map();
    cards.concat(glances).forEach(function (el) {
      var mk = document.createComment('updp-doc');
      el.parentNode.insertBefore(mk, el);
      markers.set(el, mk);
    });
    var glanceHidden = glances.map(function () { return true; });
    var byKey = {};
    cards.forEach(function (c) { byKey['d:' + c.getAttribute('data-key')] = c; });
    glances.forEach(function (g) { byKey['g:' + g.getAttribute('data-key')] = g; });
    var lastCatalog = null;

    function listOf(group) { return qs(group, '[data-updp-docgroup-list]') || group; }
    function restore(el) {
      var mk = markers.get(el);
      if (mk && mk.parentNode && el.previousSibling !== mk) mk.parentNode.insertBefore(el, mk.nextSibling);
    }
    function docData() {
      var fromIsland = {};
      ctx.island.docs.forEach(function (d) { if (d && d.key) fromIsland[d.key] = d; });
      return cards.map(function (c) {
        var key = c.getAttribute('data-key');
        var d = fromIsland[key];
        return {
          key: key,
          applies_to: d && Array.isArray(d.applies_to) ? d.applies_to : String(c.getAttribute('data-applies') || '').split(/\s+/).filter(Boolean),
          doc_type: (d && d.doc_type) || c.getAttribute('data-doc-type') || ''
        };
      });
    }
    function glanceData() {
      var fromIsland = {};
      ctx.island.glance.forEach(function (g) { if (g && g.key) fromIsland[g.key] = g; });
      return glances.map(function (g) {
        var key = g.getAttribute('data-key');
        var d = fromIsland[key];
        return { key: key, families: d && Array.isArray(d.families) ? d.families : String(g.getAttribute('data-families') || '').split('|').filter(Boolean) };
      });
    }

    function render() {
      var m = ctx.machine;
      var c = m ? m.catalog_id : null;
      if (c !== lastCatalog && othersPanel) {
        setHidden(othersPanel, true);
        if (othersToggle) othersToggle.setAttribute('aria-expanded', 'false');
      }
      lastCatalog = c;
      cards.concat(glances).forEach(restore);
      glances.forEach(function (g, i) { setHidden(g, glanceHidden[i]); });
      qsa(docs, '[data-updp-doc-includes]').forEach(function (el) { setHidden(el, true); });
      if (!m) {
        baseGroups.forEach(function (g, i) { setHidden(g, baseHidden[i]); });
        setHidden(mineGroup, true);
        setHidden(manualGroup, true);
        setHidden(othersWrap, true);
        setHidden(empty, true);
        noMachineOnly.forEach(function (el, i) { setHidden(el, noMachineHidden[i]); });
        return;
      }
      var plan = core.docsPlan(docData(), glanceData(), c, m.family, ctx.island.fit);
      var mineList = listOf(mineGroup);
      plan.mine.forEach(function (it) {
        var el = byKey[(it.kind === 'glance' ? 'g:' : 'd:') + it.key];
        if (!el || !mineList) return;
        mineList.appendChild(el);
        if (it.kind === 'glance') { fillSlots(el, 'family', m.family); setHidden(el, false); }
      });
      var manualList = listOf(manualGroup);
      plan.mymanual.forEach(function (k) { var el = byKey['d:' + k]; if (el && manualList) manualList.appendChild(el); });
      var otherList = othersPanel ? listOf(othersPanel) : null;
      plan.others.forEach(function (k) { var el = byKey['d:' + k]; if (el && otherList) otherList.appendChild(el); });
      plan.mine.concat(plan.mymanual.map(function (k) { return { key: k, kind: 'doc' }; })).forEach(function (it) {
        if (it.kind !== 'doc') return;
        var el = byKey['d:' + it.key];
        var inc = qs(el, '[data-updp-doc-includes]');
        if (inc) { fillSlots(inc, 'model', m.name); setHidden(inc, false); }
      });
      if (mineGroup) { fillSlots(mineGroup, 'model', m.name); setHidden(mineGroup, false); }
      if (manualGroup) { fillSlots(manualGroup, 'model', m.name); setHidden(manualGroup, !plan.mymanual.length); }
      if (othersWrap) {
        setHidden(othersWrap, !plan.others.length || !otherList);
        if (othersToggle) fillSlots(othersToggle, 'n', String(plan.others.length));
      }
      if (empty) {
        fillSlots(empty, 'model', m.name);
        fillSlots(empty, 'noun', ctx.island.product.noun || '');
        setHidden(empty, plan.mine.length > 0);
      }
      baseGroups.forEach(function (g) { setHidden(g, !qs(g, '[data-updp-doc]')); });
      noMachineOnly.forEach(function (el) { setHidden(el, true); });
    }
    ctx.renderers.push(render);

    if (othersToggle && othersPanel) {
      othersToggle.addEventListener('click', function (e) {
        e.preventDefault();
        var open = othersPanel.hasAttribute('hidden');
        setHidden(othersPanel, !open);
        othersToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
  }

  /* ---------- You may also need ---------- */
  function initAddons(ctx) {
    var root = ctx.root;
    var wrap = qs(root, '[data-updp-addons]');
    if (!wrap) return;
    var wrapHidden = wrap.hasAttribute('hidden');
    var cards = qsa(wrap, '[data-updp-addon]');
    var prompt = qs(wrap, '[data-updp-addon-prompt]');
    var promptHidden = prompt ? prompt.hasAttribute('hidden') : true;
    var emptyCard = qs(wrap, '[data-updp-addon-empty]');
    var fits = {};
    var liquidShown = {};
    ctx.island.addons.forEach(function (a) {
      if (!a || a.variant_id == null) return;
      fits[String(a.variant_id)] = Array.isArray(a.fit) ? a.fit : [];
      if (typeof a.shown === 'boolean') liquidShown[String(a.variant_id)] = a.shown;
    });
    function fitOf(card) {
      var id = String(card.getAttribute('data-variant-id') || '');
      if (fits[id]) return fits[id];
      return String(card.getAttribute('data-fit') || '').split(/\s+/).filter(Boolean);
    }
    /* No machine: Liquid's rule-7 result (island `shown` / data-shown) wins, so JS and Liquid never disagree. */
    function shownWithoutMachine(card) {
      var id = String(card.getAttribute('data-variant-id') || '');
      if (typeof liquidShown[id] === 'boolean') return liquidShown[id];
      var ds = card.getAttribute('data-shown');
      if (ds === 'true' || ds === 'false') return ds === 'true';
      return core.addonVisible(fitOf(card), ctx.island.fit, null);
    }
    function render() {
      var m = ctx.machine;
      var c = m ? m.catalog_id : null;
      var visible = 0;
      cards.forEach(function (card) {
        var show = m ? core.addonVisible(fitOf(card), ctx.island.fit, c) : shownWithoutMachine(card);
        setHidden(card, !show);
        if (show) visible++;
        var note = qs(card, '[data-updp-addon-note]');
        if (note) { if (m) fillSlots(note, 'model', m.name); setHidden(note, !(m && show)); }
      });
      /* the check-your-machine card: Liquid decides it without a machine; with one it has nothing to say */
      if (prompt) setHidden(prompt, m ? true : (promptHidden && visible > 0));
      if (emptyCard) setHidden(emptyCard, !(visible === 0 && m));
      /* with a machine and nothing listed for it, the band renders nothing (no gap, no filler) */
      setHidden(wrap, wrapHidden || (!!m && visible === 0 && !emptyCard));
    }
    ctx.renderers.push(render);
  }

  /* ---------- help and quote links: model=, qty=, ask reason ---------- */
  function initHelpLinks(ctx) {
    var root = ctx.root;
    function render() {
      var m = ctx.machine;
      qsa(root, 'a[data-updp-help]').forEach(function (a) {
        var href = a.getAttribute('href');
        if (!href) return;
        var kind = a.getAttribute('data-updp-help');
        /* L renders "Help identify my machine" as help="ask" inside the fit panel */
        if (kind === 'ask' && a.closest('[data-updp-fit-panel-inner]')) kind = 'identify';
        var hasQty = /[?&]qty=/.test(href);
        var next = core.rewriteHelpUrl(href, core.helpChanges(kind, hasQty, m, ctx.qty));
        if (next !== href) a.setAttribute('href', next);
      });
    }
    ctx.renderers.push(render);
    ctx.onQty = function () { render(); };
  }

  /* ---------- machine-dependent copy variants: CSS shows [data-updp-when] by the root's data-machine;
     J fills the model name (L/CONTRACT-ADDITIONS B) ---------- */
  function initMachineText(ctx) {
    var root = ctx.root;
    ctx.renderers.push(function () {
      var m = ctx.machine;
      if (!m) return;
      qsa(root, '[data-updp-when="machine"]').forEach(function (el) { fillSlots(el, 'model', m.name); });
    });
  }

  /* ---------- gallery, zoom, lightbox ---------- */
  function initGallery(ctx) {
    var root = ctx.root;
    var str = ctx.str;
    var gallery = qs(root, '[data-updp-gallery]');
    var lightbox = qs(root, 'dialog[data-updp-lightbox]');
    var lbImg = qs(lightbox, 'img[data-updp-lightbox-img]');
    if (!gallery) return;
    var slides = qsa(gallery, '[data-updp-slide]');
    var thumbs = qsa(gallery, '[data-updp-thumb]');
    var zoom = qs(gallery, '[data-updp-zoom]');
    var idxs = slides.map(function (s, i) { return s.getAttribute('data-index') || String(i); });
    var active = Math.max(0, slides.findIndex(function (s) { return !s.hasAttribute('hidden'); }));

    function show(i, focusThumb) {
      if (!slides.length) return;
      active = (i + slides.length) % slides.length;
      var key = idxs[active];
      slides.forEach(function (s, j) { setHidden(s, j !== active); });
      thumbs.forEach(function (t) {
        var on = t.getAttribute('data-index') === key;
        if (on) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
        if (on && focusThumb) focusEl(t);
      });
      qsa(gallery, '[data-slot="counter"]').forEach(function (el) {
        var cur = qs(el, '[data-slot="counter-current"]');
        if (cur) setText(cur, String(active + 1));
        else setText(el, str('counter', { n: active + 1, i: active + 1, total: slides.length }));
      });
      if (zoom) setHidden(zoom, !slides[active].hasAttribute('data-zoomable'));
    }
    show(active, false);

    thumbs.forEach(function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        var j = idxs.indexOf(t.getAttribute('data-index'));
        if (j !== -1) show(j, false);
      });
      t.addEventListener('keydown', function (e) {
        var k = e.key;
        if (k !== 'ArrowRight' && k !== 'ArrowLeft' && k !== 'Right' && k !== 'Left') return;
        e.preventDefault();
        var pos = thumbs.indexOf(t) + (k === 'ArrowRight' || k === 'Right' ? 1 : -1);
        var next = thumbs[(pos + thumbs.length) % thumbs.length];
        var j = idxs.indexOf(next.getAttribute('data-index'));
        if (j !== -1) show(j, true);
      });
    });
    qsa(gallery, '[data-updp-prev]').forEach(function (b) { b.addEventListener('click', function (e) { e.preventDefault(); show(active - 1, false); }); });
    qsa(gallery, '[data-updp-next]').forEach(function (b) { b.addEventListener('click', function (e) { e.preventDefault(); show(active + 1, false); }); });

    var sx = null;
    var sy = null;
    gallery.addEventListener('touchstart', function (e) {
      if (slides.length < 2 || !e.touches || e.touches.length !== 1 || (e.target.closest && e.target.closest('[data-updp-thumb]'))) { sx = null; return; }
      sx = e.touches[0].clientX;
      sy = e.touches[0].clientY;
    }, { passive: true });
    gallery.addEventListener('touchend', function (e) {
      if (sx == null || !e.changedTouches || !e.changedTouches.length) return;
      var dx = e.changedTouches[0].clientX - sx;
      var dy = e.changedTouches[0].clientY - sy;
      sx = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) show(active + (dx < 0 ? 1 : -1), false);
    }, { passive: true });

    function closeLightbox() {
      if (!lightbox) return;
      closeDialog(lightbox);
      onLightboxClosed();
    }
    function onLightboxClosed() {
      if (!ctx.bars || !ctx.bars.lightboxOpen) return;
      ctx.bars.lightboxOpen = false;
      ctx.updateBars();
      if (zoom) focusEl(zoom);
    }
    if (zoom && lightbox) {
      zoom.addEventListener('click', function (e) {
        e.preventDefault();
        var s = slides[active];
        var img = s && (s.tagName === 'IMG' ? s : qs(s, 'img'));
        var src = (s && s.getAttribute('data-zoom-src')) || (img && (img.getAttribute('data-zoom-src') || img.currentSrc || img.getAttribute('src'))) || '';
        if (!src || !lbImg) return;
        lbImg.setAttribute('src', src);
        lbImg.setAttribute('alt', (s && s.getAttribute('data-zoom-alt')) || (img ? img.getAttribute('alt') || '' : ''));
        openDialog(lightbox);
        if (ctx.bars) { ctx.bars.lightboxOpen = true; ctx.updateBars(); }
        var closeBtn = qs(lightbox, '[data-updp-lightbox-close]');
        if (closeBtn) setTimeout(function () { focusEl(closeBtn); }, 0);
      });
      qsa(lightbox, '[data-updp-lightbox-close]').forEach(function (b) {
        b.addEventListener('click', function (e) { e.preventDefault(); closeLightbox(); });
      });
      lightbox.addEventListener('click', function (e) { if (e.target === lightbox) closeLightbox(); });
      lightbox.addEventListener('close', onLightboxClosed);
    }
  }

  /* ---------- SKU copy ---------- */
  function initSkuCopy(ctx) {
    var root = ctx.root;
    function legacyCopy(text, btn) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
      document.body.removeChild(ta);
      focusEl(btn);
      return ok;
    }
    qsa(root, '[data-updp-copy-sku]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var sku = btn.getAttribute('data-sku') || ctx.island.product.sku || '';
        if (!sku) return;
        var done = function (ok) {
          if (!ok) return;
          ctx.announce(ctx.str('sku_copied'));
          btn.setAttribute('data-copied', 'true');
          setTimeout(function () { btn.removeAttribute('data-copied'); }, 2000);
        };
        var clip = navigator.clipboard;
        if (clip && typeof clip.writeText === 'function' && window.isSecureContext !== false) {
          clip.writeText(sku).then(function () { done(true); }, function () { done(legacyCopy(sku, btn)); });
        } else {
          done(legacyCopy(sku, btn));
        }
      });
    });
  }

  /* ---------- mobile accordions: closed on load, always open ≥750 px ---------- */
  function initAccordions(ctx) {
    var root = ctx.root;
    var toggles = qsa(root, '[data-updp-acc-toggle]');
    function panelOf(t) { return document.getElementById(t.getAttribute('aria-controls') || ''); }
    function sync() {
      toggles.forEach(function (t) {
        var p = panelOf(t);
        if (!p) return;
        setHidden(p, isMobile() ? t.getAttribute('aria-expanded') !== 'true' : false);
      });
    }
    toggles.forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
      t.addEventListener('click', function (e) {
        e.preventDefault();
        t.setAttribute('aria-expanded', t.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
        sync();
      });
    });
    ctx.openAccordionFor = function (target) {
      if (!isMobile()) return false;
      for (var i = 0; i < toggles.length; i++) {
        var p = panelOf(toggles[i]);
        if (p && (p.contains(target) || target.contains(p))) {
          if (toggles[i].getAttribute('aria-expanded') === 'true') return false;
          toggles[i].setAttribute('aria-expanded', 'true');
          sync();
          return true;
        }
      }
      return false;
    };
    ctx.accToggleFor = function (target) {
      for (var i = 0; i < toggles.length; i++) {
        var p = panelOf(toggles[i]);
        if (p && (p.contains(target) || target.contains(p))) return toggles[i];
      }
      return null;
    };
    onMqChange(mobileMq, sync);
    sync();
  }

  /* ---------- section nav, in-page links, aria-current ---------- */
  function initSectionLinks(ctx) {
    var root = ctx.root;
    var secbar = qs(root, '[data-updp-secbar]');
    var navLinks = qsa(secbar, 'a[data-updp-nav]');
    var sections = SECTION_IDS.map(function (id) { var el = document.getElementById(id); return el && root.contains(el) ? el : null; });

    function barHeight() {
      if (!secbar) return 0;
      var cs;
      try { cs = window.getComputedStyle(secbar); } catch (e) { return 0; }
      if (cs.display === 'none' || (cs.position !== 'sticky' && cs.position !== 'fixed')) return 0;
      var v = parseFloat(window.getComputedStyle(root).getPropertyValue('--updp-bar-h')) || 0;
      return Math.max(v, secbar.getBoundingClientRect().height || 0);
    }
    function offset() { return (ctx.headerH || 0) + barHeight() + 16; }
    function scrollToEl(el) {
      var top = el.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop || 0) - offset();
      top = Math.max(0, Math.round(top));
      try { window.scrollTo({ top: top, behavior: reducedMotion() ? 'auto' : 'smooth' }); }
      catch (e) { try { window.scrollTo(0, top); } catch (e2) { /* jsdom */ } }
    }
    function focusHeading(id, target) {
      var h = document.getElementById(HEADING_IDS[id]);
      if (h && rendered(h)) { focusEl(h); return; }
      var t = ctx.accToggleFor ? ctx.accToggleFor(target) : null;
      if (t) focusEl(t);
      else if (h) focusEl(h);
    }
    function goTo(id) {
      var target = document.getElementById(id);
      if (!target || !root.contains(target)) return false;
      var opened = ctx.openAccordionFor ? ctx.openAccordionFor(target) : false;
      var run = function () {
        var dest = target;
        if (id === 'compatibility' && isMobile()) {
          var pinned = qs(root, '[data-updp-family][data-pinned]');
          if (pinned) dest = pinned;
        }
        scrollToEl(dest);
        focusHeading(id, target);
        setCurrent(id);
        holdCurrent(id, dest);
      };
      if (opened) raf(run); else run();
      return true;
    }
    ctx.goToSection = goTo;

    root.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a');
      if (!a || !root.contains(a)) return;
      var id = a.getAttribute('data-updp-nav');
      if (!id) {
        var href = a.getAttribute('href') || '';
        if (href.charAt(0) !== '#') return;
        id = href.slice(1);
      }
      if (SECTION_IDS.indexOf(id) === -1) return;
      if (goTo(id)) e.preventDefault();
    });

    function setCurrent(id) {
      navLinks.forEach(function (a) {
        if (a.getAttribute('data-updp-nav') === id) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });
    }
    /* The V3 header compacts (173 → 117 px) while the page scrolls, so a jump aimed at the start lands a few px short
       and recompute() would then mark the previous section (release theme, audit 3 W3). Hold the chosen section until
       the scroll has ended and the header has settled, then re-aim once and release. */
    var hold = null;
    function holdCurrent(id, dest) {
      if (hold) { clearTimeout(hold.timer); window.removeEventListener('scrollend', hold.onEnd); }
      var h = { id: id };
      var release = function () {
        if (hold !== h) return;
        window.removeEventListener('scrollend', h.onEnd);
        clearTimeout(h.timer);
        hold = null;
        var miss = 0;
        try { miss = dest.getBoundingClientRect().top - offset(); } catch (e) { miss = 0; }
        if (Math.abs(miss) > 2) {
          var y = Math.max(0, Math.round((window.pageYOffset || document.documentElement.scrollTop || 0) + miss));
          try { window.scrollTo({ top: y, behavior: 'auto' }); } catch (e) { try { window.scrollTo(0, y); } catch (e2) { /* jsdom */ } }
        }
        setCurrent(id);
      };
      h.onEnd = function () { clearTimeout(h.timer); h.timer = setTimeout(release, 250); };
      h.timer = setTimeout(release, 1500);
      window.addEventListener('scrollend', h.onEnd);
      hold = h;
    }
    function recompute() {
      if (!navLinks.length) return;
      if (hold) { setCurrent(hold.id); return; }
      var line = offset() + 8;
      var cur = SECTION_IDS[0];
      sections.forEach(function (s, i) {
        if (s && s.getBoundingClientRect().top <= line) cur = SECTION_IDS[i];
      });
      setCurrent(cur);
    }
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      raf(function () { ticking = false; recompute(); });
    }
    if (navLinks.length) {
      if (typeof window.IntersectionObserver === 'function') {
        var th = [];
        for (var i = 0; i <= 20; i++) th.push(i / 20);
        var io = new IntersectionObserver(function () { onScroll(); }, { threshold: th });
        sections.forEach(function (s) { if (s) io.observe(s); });
      } else {
        window.addEventListener('scroll', onScroll, { passive: true });
      }
      window.addEventListener('resize', onScroll);
      recompute();
    }

    /* arriving with #compatibility etc. on a phone: open that accordion */
    function fromHash() {
      var id = (window.location.hash || '').slice(1);
      if (SECTION_IDS.indexOf(id) !== -1 && isMobile()) goTo(id);
    }
    window.addEventListener('hashchange', fromHash);
    if (window.location && window.location.hash) raf(fromHash);
  }

  /* ---------- Webflow's anchor scroll ----------
     The theme loads the Webflow runtime (assets/h1-custom.js). Its "scroll" module takes every same-page `#id` link
     through a jQuery handler on document ("click.wf-scroll"), ignores preventDefault, and animates the target to the
     top of the window with no header offset, so our section links land under the sticky V3 header and section bar
     (found on the release theme 2026-09-27, qa-draft I2). This script only loads on the part template, so remove
     that one handler here: on load, when Webflow is ready, and on window load. Nothing else is touched. */
  function disableWebflowAnchorScroll() {
    var off = function () {
      try { if (window.jQuery) window.jQuery(document).off('click.wf-scroll'); } catch (e) { /* no jQuery */ }
    };
    off();
    try { if (window.Webflow && typeof window.Webflow.push === 'function') window.Webflow.push(off); } catch (e) { /* no Webflow */ }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(off, 0); });
    window.addEventListener('load', off);
  }

  /* ---------- boot ---------- */
  function boot(scope) {
    qsa(scope || document, '[data-updp]').forEach(initRoot);
  }
  disableWebflowAnchorScroll();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  window.ULSPartPDP = { core: core, init: boot };
})();
